import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const onSubmit = async (data) => {
    const result = await login(data);
    if (result.success) {
      navigate("/dashboard");
    } else if (result.requires2FA) {
      navigate("/2fa-challenge", { state: { tempToken: result.tempToken } });
    } else if (result.requiresVerification) {
      const email = result.email || data.email;
      const target = email
        ? `/verify-email?email=${encodeURIComponent(email)}`
        : "/verify-email";
      navigate(target, { state: { email } });
    } else {
      setError("root", { message: result.error });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020617] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.25)_1px,_transparent_0)] h-full w-full [background-size:40px_40px]" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-slate-900/70 p-8 shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
          <div className="space-y-2 text-center">
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
              Authentication
            </p>
            <h2 className="text-3xl font-semibold text-white">
              {t("auth.login.title")}
            </h2>
            <p className="text-sm text-slate-400">{t("auth.login.subtitle")}</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.login.email")}
                </label>
                <div className="relative">
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    type="email"
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="you@company.ch"
                  />
                  <Mail className="input-icon-left text-slate-400" />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.login.password")}
                </label>
                <div className="relative">
                  <input
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="••••••••"
                  />
                  <Lock className="input-icon-left text-slate-400" />
                  <button
                    type="button"
                    className="input-icon-right text-slate-300 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            {errors.root && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                {errors.root.message}
              </div>
            )}

            <div>
              <Link
                to="/forgot-password"
                className="text-sm text-cyan-200 hover:text-white"
              >
                {t("auth.login.forgotPassword")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                t("auth.login.submit")
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
